import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Search, ShieldCheck } from "lucide-react";
import { api } from "../api";

export default function CoverageChecker() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setShowSug(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await api.coverage(query);
        setSuggestions(data.matches);
        setShowSug(true);
      } catch {
        setSuggestions([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setShowSug(false);
    setBusy(true);
    try {
      setRes(await api.coverage(q.trim()));
    } catch {
      setRes({ matches: [], count: 0, query: q });
    }
    setBusy(false);
  };

  return (
    <section className="border-b border-white/10 bg-coal/40 tactical-grid" data-testid="coverage-checker">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-amber" /> Coverage Checker
            </p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight">
              Do we cover your area?
            </h2>
            <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed max-w-md">
              Enter your town, city or postcode and jump straight to your local security pages — 210+ UK locations,
              17 specialist services, one standard.
            </p>
          </div>
          <div>
            <form onSubmit={search} className="relative flex border border-white/15 bg-ink focus-within:border-amber transition-colors duration-300">
              <div className="flex items-center pl-4 text-gray-500">
                <Search className="w-4 h-4" />
              </div>
              <input
                data-testid="coverage-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => suggestions.length && setShowSug(true)}
                onBlur={() => setTimeout(() => setShowSug(false), 200)}
                placeholder="e.g. Leeds, Glasgow, M1 2AB, CF10…"
                autoComplete="off"
                className="flex-1 bg-transparent px-4 py-4 text-sm text-white placeholder-gray-600 outline-none font-body"
              />
              {showSug && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 border border-amber/40 bg-card shadow-2xl shadow-black/60 z-40" data-testid="coverage-suggestions">
                  {suggestions.map((m) => (
                    <button
                      key={m.slug}
                      type="button"
                      data-testid={`coverage-suggestion-${m.slug}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setShowSug(false);
                        navigate(`/locations/${m.slug}`);
                      }}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-raised transition-colors group"
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <MapPin className="w-3.5 h-3.5 text-amber shrink-0" />
                        <span className="font-display font-bold uppercase tracking-wide text-sm text-white group-hover:text-amber transition-colors truncate">
                          Security Guards {m.name}
                        </span>
                      </span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500 shrink-0">{m.county}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="submit"
                data-testid="coverage-submit"
                className="bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-6 hover:bg-white transition-colors duration-300"
              >
                {busy ? "…" : "Check"}
              </button>
            </form>

            {res && (
              <div className="mt-4 space-y-2" data-testid="coverage-results">
                {res.matches.length === 0 && (
                  <p className="text-sm text-gray-400 border border-white/10 bg-card px-4 py-4" data-testid="coverage-no-match">
                    No direct match for “{res.query}” — but we cover 210+ UK locations.{" "}
                    <Link to="/locations" className="text-amber underline underline-offset-4">Browse the full directory</Link>.
                  </p>
                )}
                {res.matches.map((m) => (
                  <div
                    key={m.slug}
                    data-testid={`coverage-match-${m.slug}`}
                    className="border border-white/10 bg-card px-4 py-4 hover:border-amber/60 transition-colors duration-300"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <Link to={`/locations/${m.slug}`} className="group flex items-center gap-2.5">
                        <MapPin className="w-4 h-4 text-amber shrink-0" />
                        <span className="font-display font-bold uppercase tracking-wide text-lg text-white group-hover:text-amber transition-colors">
                          Security Guards {m.name}
                        </span>
                        <ArrowRight className="w-4 h-4 text-amber opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                        {m.county} // {m.region}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> {m.note}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to={`/locations/${m.slug}/manned-guarding`}
                        data-testid={`coverage-manned-${m.slug}`}
                        className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-white/10 text-gray-400 hover:border-amber hover:text-amber transition-colors"
                      >
                        Manned Guarding {m.name}
                      </Link>
                      <Link
                        to={`/locations/${m.slug}/cctv-monitoring`}
                        data-testid={`coverage-cctv-${m.slug}`}
                        className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-white/10 text-gray-400 hover:border-amber hover:text-amber transition-colors"
                      >
                        CCTV Monitoring {m.name}
                      </Link>
                      <Link
                        to={`/locations/${m.slug}/mobile-security-patrols`}
                        data-testid={`coverage-patrols-${m.slug}`}
                        className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-white/10 text-gray-400 hover:border-amber hover:text-amber transition-colors"
                      >
                        Mobile Patrols {m.name}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
